import { Clock, Rows3, FileText, HardDrive, Bot, AlertTriangle } from 'lucide-react';
import { StatusBarContextPicker } from './StatusBarContextPicker';
import { IntelliSenseStatusIndicator } from './IntelliSenseStatusIndicator';
import { useAiLockStore } from '../../../terminal/store/aiLockStore';
import type { IntelliSenseStatus } from '../../../connections/services/language-services/languageServiceProvider';
import { getFileNameFromPath } from '../utils/queryFileUtils';
import type { TransactionMode, TransactionState } from '../../core/types/workspaceTypes';
import { computeTransactionIndicator } from '../utils/transactionIndicator';
import { formatRowCount } from '../../common/utils/formatRowCount';
import { computeQueryExecutionEnergyState } from '../../common/utils/queryExecutionEnergy';

interface QueryStatusBarProps {
  message: string;
  tabId?: string;
  filePath?: string;
  isFileDirty?: boolean;
  connectionId?: string;
  connectionName?: string;
  serverHost?: string;
  database?: string;
  executionTime?: string;
  /**
   * Set after the long-running threshold is crossed — turns the timer text
   * (and its Clock glyph) onto the shared --energy family (CH-28) without
   * reflowing the status row. Returns to neutral the instant execution ends.
   */
  isLongRunning?: boolean;
  /**
   * True once the tab has run at least one query. Before that, the elapsed
   * timer is hidden entirely rather than showing a `00:00:00` that implies a
   * measurement that never happened.
   */
  hasExecuted?: boolean;
  rowCount?: number;
  totalRowCount?: number;
  isTruncated?: boolean;
  /**
   * Rows the last run's INSERT / UPDATE / DELETE statements changed, passed
   * only when it returned no result set — the row counter then reads
   * `N rows affected` instead of `0 rows`.
   */
  affectedRowCount?: number;
  /** Current scroll position (first visible row, 0-based) — shown when totalRowCount is large */
  scrollRow?: number;
  onDatabaseChange?: (database: string) => void;
  /** Increment to pop the database selector open (query-error "Switch database…" action). */
  databaseSelectorOpenNonce?: number;
  onConnectionChange?: (connectionId: string, connectionName: string, serverHost: string) => void;
  onAddConnection?: () => void;
  intelliSenseStatus?: IntelliSenseStatus;
  intelliSenseMessage?: string;
  /**
   * Force-refresh the IntelliSense schema snapshot — offered by the `partial`
   * pill state only (S7 item 5), where a row cap clipped part of the schema.
   */
  onReloadIntelliSenseSchema?: () => void;
  /** True while a reload triggered from the pill is still in flight. */
  isReloadingIntelliSenseSchema?: boolean;
  transactionMode?: TransactionMode;
  transactionState?: TransactionState;
  connectionColor?: string;
  className?: string;
}

export function QueryStatusBar({
  message = 'Ready',
  tabId,
  filePath,
  isFileDirty = false,
  connectionId,
  connectionName,
  serverHost,
  database,
  executionTime = '00:00:00',
  isLongRunning = false,
  hasExecuted = false,
  rowCount = 0,
  totalRowCount,
  isTruncated = false,
  affectedRowCount,
  scrollRow,
  onDatabaseChange,
  databaseSelectorOpenNonce,
  onConnectionChange,
  onAddConnection,
  intelliSenseStatus,
  intelliSenseMessage,
  onReloadIntelliSenseSchema,
  isReloadingIntelliSenseSchema = false,
  transactionMode,
  transactionState,
  connectionColor,
  className = '',
}: QueryStatusBarProps) {
  // Check if this tab is locked by AI
  const lockInfo = useAiLockStore((s) => (tabId ? s.lockedTabs.get(tabId) : undefined));
  const isLockedByAi = !!lockInfo;

  // Show scroll position when there are enough rows to make it useful (>1000)
  const effectiveTotal = typeof totalRowCount === 'number' && totalRowCount !== rowCount
    ? totalRowCount
    : rowCount;
  const showScrollPosition = typeof scrollRow === 'number' && effectiveTotal > 1000;

  const rowCountLabel = typeof affectedRowCount === 'number'
    ? `${formatRowCount(affectedRowCount)} affected`
    : showScrollPosition
      ? `Row ${(scrollRow + 1).toLocaleString()} of ${effectiveTotal.toLocaleString()}`
      : typeof totalRowCount === 'number' && totalRowCount !== rowCount
        ? `${rowCount.toLocaleString()}/${totalRowCount.toLocaleString()} rows${isTruncated ? ' (preview)' : ''}`
        : formatRowCount(rowCount);

  const fileName = filePath ? getFileNameFromPath(filePath) : '';

  // CH-28 — energized only once the long-running threshold is crossed
  // (computeQueryExecutionEnergyState), neutral before that and after
  // completion (isLongRunning resets to false the instant execution ends).
  const executionEnergized = computeQueryExecutionEnergyState(isLongRunning) === 'energy';

  return (
    <div
      data-testid="query-status-bar"
      className={`relative flex items-center h-full px-3 text-[12px] bg-muted/40 border-t border-border ${className}`}
    >
      {/* Connection color tint overlay */}
      {connectionColor && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundColor: connectionColor, opacity: 0.07 }}
          data-testid="status-bar-color-tint"
        />
      )}
      {/* Message Section — CH-25: `select-none` is safe here because every
          caller sets this from a small fixed vocabulary ("Ready", "Query
          executed successfully", "Query cancelled", "Query failed", the
          windowed "Loading... N / M rows" readout) — never raw driver/user
          error text, which lives elsewhere. This is a status-bar *label*,
          not a value. */}
      <div
        data-testid="status-bar-message"
        className="flex-[1_1_160px] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground select-none"
      >
        {message}
      </div>

      {/* Divider */}
      <div className="w-px h-3 bg-border mx-3" />

      {/* File Binding Section */}
      <div
        data-testid="status-bar-file"
        className="flex items-center gap-1 flex-shrink-0 text-muted-foreground"
        title={
          filePath
            ? `${filePath}${isFileDirty ? ' (unsaved changes)' : ''}`
            : 'Not tied to a file (stored in session)'
        }
      >
        {filePath ? <FileText className="w-3 h-3" /> : <HardDrive className="w-3 h-3" />}
        <span className="max-w-36 truncate">
          {filePath ? fileName : 'Session'}
        </span>
      </div>

      {/* Divider */}
      <div className="w-px h-3 bg-border mx-3" />

      {/* Connection › Database */}
      <StatusBarContextPicker
        connectionId={connectionId}
        connectionName={connectionName}
        serverHost={serverHost}
        connectionColor={connectionColor}
        database={database}
        onConnectionChange={onConnectionChange}
        onAddConnection={onAddConnection}
        onDatabaseChange={onDatabaseChange}
        databaseSelectorOpenNonce={databaseSelectorOpenNonce}
      />

      {/* Divider */}
      <div className="w-px h-3 bg-border mx-3" />

      {/* IntelliSense Status Section */}
      {intelliSenseStatus && (
        <div data-testid="status-bar-intellisense">
          <IntelliSenseStatusIndicator
            status={intelliSenseStatus}
            message={intelliSenseMessage}
            onReloadSchema={onReloadIntelliSenseSchema}
            isReloadingSchema={isReloadingIntelliSenseSchema}
          />
        </div>
      )}

      {/* Divider - only show if IntelliSense status is displayed */}
      {intelliSenseStatus && <div className="w-px h-3 bg-border mx-3" />}

      {/* Transaction Indicator */}
      {transactionMode && transactionState && (() => {
        const indicator = computeTransactionIndicator(transactionMode, transactionState);
        return (
          <>
            {/* CH-25: fixed-vocabulary label ("Tx: Auto/Manual/Smart
                (Idle/Active/Error)") — a status-bar label, not a value. */}
            <div
              data-testid="status-bar-transaction"
              className={`flex items-center gap-1 flex-shrink-0 font-medium select-none ${indicator.colorClass}`}
              title={indicator.tooltip}
            >
              {/* G-5 (T6.1b): gated explicitly on `transactionMode !==
                  'auto'`, matching the tab dot and toolbar dot — the text
                  label already goes quiet in Auto via
                  `computeTransactionIndicator` (unaffected here), but this
                  round dot bypassed that computation and checked raw
                  `transactionState` only. Auto-commit never leaves a
                  lingering open transaction, so the dot must not show even
                  if some producer reports `active` while mode is still
                  `auto` (verified reachable via the MCP `transaction_begin`
                  tool — see task-T6.1b-report.md). `animate-pulse` is the
                  one treatment this task picked across all three
                  transaction surfaces for "genuinely open" — this site
                  already had it; the tab and toolbar dots were brought
                  onto it here rather than the reverse. */}
              {transactionMode !== 'auto' && transactionState === 'active' && (
                <span className="h-2 w-2 rounded-full bg-energy animate-pulse" />
              )}
              {transactionMode !== 'auto' && transactionState === 'error' && (
                <span className="h-2 w-2 rounded-full bg-destructive" />
              )}
              <span>{indicator.text}</span>
            </div>
            <div className="w-px h-3 bg-border mx-3" />
          </>
        );
      })()}

      {/* AI Editing Indicator */}
      {isLockedByAi && (
        <>
          {/* CH-25: fixed "AI editing" badge text — a status-bar label, not
              a value (the tab name it references lives in the tooltip only,
              via `title`, not as rendered text here). */}
          <div
            data-testid="status-bar-ai-editing"
            className="flex items-center gap-1 flex-shrink-0 text-purple-500 select-none"
            title={`AI is editing this tab${lockInfo?.tabName ? ` (${lockInfo.tabName})` : ''}`}
          >
            <Bot className="w-3 h-3 animate-pulse" />
            <span className="font-medium">AI editing</span>
          </div>
          <div className="w-px h-3 bg-border mx-3" />
        </>
      )}

      {/* Row count + elapsed time — one calm readout, "266 rows · 00:00:03",
          instead of two separately-divided sections. The row count stays
          visible from the start (it has a real, meaningful zero); the timer
          joins only once a query has actually run — a `00:00:00` before that
          would just be noise implying a measurement that never happened.

          `flex-shrink-0`: the row count is short, high-value text, so it must
          NOT be the thing that collapses when the bar runs out of width. The
          free-form message spacer (`flex-[1_1_160px]`) is the low-value item
          that yields and ellipsises first. Without this, a narrow window — a
          small screen, or the app un-maximised with the sidebar open — starved
          this group to `min-width: 0` and the whole readout (icon included)
          vanished, instead of the message truncating. */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {/* Row Count Section. Truncation is a "you are not seeing everything"
            warning, so it takes the shared energy hue (CH-09 §6 item 7) rather
            than a one-off amber. Meaning is unchanged — hue only. This is small
            BOLD TEXT, so it uses --energy-text (the AA-rated variant), not the
            glyph/wash --energy. */}
        <div
          data-testid="status-bar-row-count"
          className={`flex items-center gap-1 min-w-0 ${isTruncated ? 'text-energy-text font-semibold' : 'text-muted-foreground'}`}
          title={isTruncated ? 'Showing a partial preview — not all matching rows are loaded' : undefined}
        >
          {isTruncated ? (
            <AlertTriangle
              className="w-3 h-3 flex-shrink-0"
              role="img"
              aria-label="Partial results — showing a preview, not all matching rows"
            />
          ) : (
            <Rows3 className="w-3 h-3 flex-shrink-0" />
          )}
          <span className="min-w-0 truncate tabular-nums">{rowCountLabel}</span>
        </div>

        {hasExecuted && (
          <>
            <span className="text-muted-foreground" aria-hidden="true">·</span>

            {/* Execution Time Section — CH-28 flagship placement: the Clock
                glyph takes --energy, the elapsed-time text takes the
                AA-rated --energy-text (small bold text needs the deeper
                variant — see src/index.css's --energy-text comment), and
                both stay neutral outside the long-running window. */}
            <div
              data-testid="status-bar-execution-time"
              data-long-running={isLongRunning || undefined}
              className="flex items-center gap-1 flex-shrink-0"
              title={isLongRunning ? 'Query is taking longer than expected' : undefined}
            >
              <Clock className={`w-3 h-3 ${executionEnergized ? 'text-energy' : 'text-muted-foreground'}`} />
              <span className={`tabular-nums ${executionEnergized ? 'text-energy-text font-medium' : 'text-muted-foreground'}`}>
                {executionTime}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
